/**
 * Error Capture - Local-Only Sentry, First-Party Reporting
 *
 * Initializes Sentry purely for local breadcrumb/context tracking.
 * NO data is ever sent to Sentry's servers:
 *   - Empty DSN prevents server connection
 *   - No-op transport blocks all network requests
 *   - beforeSend returns null as a final safeguard
 *
 * Global errors are reported to our own backend: on the S1 site through the bug report system, for AD users
 * only; on a customer site as a scrubbed error report that carries no request or response bodies.
 */

import { AuthenticationType } from "@appEnum/AuthenticationType";
import { SiteType } from "@appEnum/SiteType";
import { useBugReport } from "@compUtility/bugReportTool/composables/bugReport";
import * as Sentry from "@sentry/vue";
import { useAppStore } from "@stores/appStore";
import { activityTracker } from "@utility/activityTracker";
import { fetchData } from "@utility/webServices";
import type { App } from "vue";
import {
  buildClientErrorReport,
  CLIENT_ERROR_REPORT_ENDPOINT,
  createClientErrorReportGate,
} from "./clientErrorPayload";

type CapturedErrorSource = "frontendRouting" | "promise";
type ErrorSink = (source: CapturedErrorSource, error: unknown) => Promise<void>;

const { reportBug } = useBugReport();

/**
 * Starts Sentry in local-only mode and routes every uncaught error on the page to the site's sink.
 * @param app The routed Vue app.
 * @param siteType The site the app runs on. Only the S1 and customer sites report.
 */
export const initErrorCapture = (app: App, siteType: SiteType): void => {
  initSentry(app);
  setGlobalErrorHandlers(
    app,
    siteType === SiteType.S1 ? silentlyReportToBugReportLog : createCustomerErrorSink()
  );
};

const initSentry = (app: App): void => {
  Sentry.init({
    app,

    // Empty DSN — Sentry won't attempt to resolve or connect to any ingest endpoint
    dsn: "",

    // No-op transport — even if a DSN were set, no HTTP requests can be made
    transport: () => ({
      send: () => Promise.resolve({}),
      flush: () => Promise.resolve(true),
    }),

    enabled: true,
    integrations: [],

    // All sampling rates at 0 — no traces, replays, or session data
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Final safeguard — drop every event so nothing is ever enqueued for sending
    beforeSend() {
      return null;
    },

    beforeBreadcrumb(breadcrumb) {
      return breadcrumb;
    },

    attachStacktrace: true,
    maxBreadcrumbs: 50,
  });

  console.info("[Sentry] Initialized in LOCAL-ONLY mode - no network calls will be made");
};

const setGlobalErrorHandlers = (app: App, sink: ErrorSink): void => {
  app.config.errorHandler = async (err) => {
    console.error("[Global Error] Vue error captured:", err);

    Sentry.captureException(err, {
      tags: { source: "vue-error-handler" },
    });

    await sink("frontendRouting", err);
  };

  window.addEventListener("unhandledrejection", async (event) => {
    console.error("[Global Error] Unhandled promise rejection captured:", event.reason);

    Sentry.captureException(event.reason, {
      tags: { source: "unhandled-promise" },
    });

    await sink("promise", event.reason);
  });

  window.addEventListener("error", async (event) => {
    console.error("[Global Error] Window error captured:", event.error || event.message);

    if (event.error) {
      Sentry.captureException(event.error, {
        tags: { source: "window-error" },
      });

      await sink("frontendRouting", event.error);
    }
  });
};

const silentlyReportToBugReportLog: ErrorSink = async (source, error) => {
  const { appArgs } = useAppStore();
  if (
    (appArgs?.CurrentUserObj?.BackingAuthSystem ?? AuthenticationType.Unknown) !==
    AuthenticationType.AuthenticateViaActiveDirectory
  ) {
    console.info(
      `[Bug Report] Skipping silent report as user is not logged in or the backing auth system is not supported`
    );
    return;
  }

  try {
    await reportBug({
      source,
      error,
      description: `Auto-reported ${source} error`,
      includeScreenshot: false,
      screenshotBase64: null,
      submitTicket: false,
    });

    console.log(`[Bug Report] Silently logged ${source} error to backend`);
  } catch (reportError) {
    console.error("[Bug Report] Failed to submit bug report:", reportError);
  }
};

const createCustomerErrorSink = (): ErrorSink => {
  const gate = createClientErrorReportGate();

  return async (_source, error) => {
    const report = buildClientErrorReport(error, window.location.pathname, activityTracker.getApiCalls());
    if (report === null || !gate.shouldReport(report)) {
      return;
    }

    try {
      await fetchData<boolean>(CLIENT_ERROR_REPORT_ENDPOINT, report, () => undefined);
    } catch {
      console.warn("[Error Capture] Could not send the error report");
    }
  };
};
